import { StarFilled, StarOutlined } from "@ant-design/icons"
import { Tag, Tooltip } from "antd"
import { type CustomTagProps } from "rc-select/lib/BaseSelect"
import React from "react"

import {
  ResolvedSelect,
  type ResolvedSelectProps
} from "../../../../../../components/Select/ResolvedSelect.tsx"
import { UNRESOLVED_USER_LABEL } from "../../../../../../utils/select/entity-labels.ts"

interface Entity {
  id: string
  is_main: boolean
}

interface ManagersSelectorProps
  extends Omit<ResolvedSelectProps, "value" | "onChange" | "mode"> {
  value?: Entity[]
  onChange?: (value: Entity[]) => void
}

export function ManagersSelector({
  value = [],
  onChange,
  ...props
}: ManagersSelectorProps) {
  const selectedIds = value.map((m) => m.id)
  const mainManagerId = value.find((m) => m.is_main)?.id ?? null

  const handleManagersChange = (ids: string[]) => {
    const currentMainId = mainManagerId

    const newValue: Entity[] = ids.map((id) => ({
      id,
      is_main: currentMainId === id || (!currentMainId && ids[0] === id)
    }))

    if (currentMainId && !ids.includes(currentMainId) && ids.length > 0) {
      newValue[0].is_main = true
    }

    const mainCount = newValue.filter((m) => m.is_main).length
    if (mainCount > 1) {
      newValue.forEach((m, i) => {
        m.is_main = i === 0
      })
    }

    onChange?.(newValue)
  }

  const handleSetMain = (id: string) => {
    const newValue = value.map((m) => ({
      ...m,
      is_main: m.id === id
    }))
    onChange?.(newValue)
  }

  const tagRender = (tagProps: CustomTagProps) => {
    const { label, value: tagValue, closable, onClose } = tagProps
    const isMain = tagValue === mainManagerId

    return (
      <Tag
        closable={closable}
        onClose={onClose}
        className="flex gap-2 items-center"
        style={{ marginInlineEnd: 4 }}
      >
        <div
          className={`${props.disabled ? "" : "cursor-pointer"}`}
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (props.disabled) return
            handleSetMain(tagValue as string)
          }}
        >
          <Tooltip title={isMain ? "Main manager" : "Set as main"}>
            {isMain ? (
              <StarFilled style={{ color: "#faad14" }} />
            ) : (
              <StarOutlined style={{ color: "#d9d9d9" }} />
            )}
          </Tooltip>
        </div>
        {label}
      </Tag>
    )
  }

  return (
    <ResolvedSelect
      allowClear
      fallbackLabel={UNRESOLVED_USER_LABEL}
      mode="multiple"
      value={selectedIds}
      onChange={handleManagersChange}
      tagRender={tagRender}
      filterOption={(input, option) =>
        ((option?.label as string) ?? "")
          ?.toLowerCase()
          .includes(input.toLowerCase())
      }
      {...props}
    />
  )
}
